import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError } from '../api/client.ts';
import { LionMark } from '../components/LionMark.tsx';

type Info = { family_name: string; inviter_name: string; expires_at: number };

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string>('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api<Info>(`/api/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'failed'));
  }, [token]);

  async function submit() {
    if (!token || !name.trim()) return;
    setSubmitting(true);
    try {
      await api('/api/auth/accept', {
        method: 'POST',
        json: { token, displayName: name.trim() },
      });
      nav('/', { replace: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'failed';
      setErr(msg);
      setSubmitting(false);
    }
  }

  if (err) {
    return (
      <Wrap>
        <LionMark size={56} />
        <h1 className="lt-serif" style={{ fontSize: 26, fontWeight: 500, margin: '20px 0 6px' }}>
          Can't open invite
        </h1>
        <p style={{ color: 'var(--danger)' }}>{err}</p>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>
          Ask {info?.inviter_name ?? 'the inviter'} for a new link.
        </p>
      </Wrap>
    );
  }

  if (!info) {
    return (
      <Wrap>
        <LionMark size={56} />
        <p style={{ color: 'var(--text-3)', marginTop: 16 }}>Looking up invite…</p>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <LionMark size={56} />
      <h1 className="lt-serif" style={{ fontSize: 28, fontWeight: 500, margin: '20px 0 6px' }}>
        Join {info.family_name}
      </h1>
      <p style={{ color: 'var(--text-2)', marginBottom: 28 }}>
        {info.inviter_name} invited you. What should we call you?
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: 340,
          padding: '14px 16px',
          borderRadius: 16,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-strong)',
        }}
      >
        <label htmlFor="name" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
          Display name
        </label>
        <input
          id="name"
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jana"
          style={{
            display: 'block',
            width: '100%',
            marginTop: 6,
            fontSize: 17,
            color: 'var(--text-1)',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
      </div>

      <button
        type="button"
        className="lt-btn lt-btn-primary"
        style={{ width: '100%', maxWidth: 340, marginTop: 14, padding: '14px 0' }}
        disabled={!name.trim() || submitting}
        onClick={submit}
      >
        {submitting ? 'Joining…' : `Join ${info.family_name}`}
      </button>
    </Wrap>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 32,
        textAlign: 'center',
        background: 'var(--bg-app)',
        color: 'var(--text-1)',
      }}
    >
      {children}
    </div>
  );
}
